import { Button } from '@lokalise/louis'
import type {
  AiConfigLanguage,
  AiConfigProject,
  Integrations,
} from '@lokalise/translation-api-schemas'
import clsx from 'clsx'
import { AI_PROFILES_DOCUMENTATION_URL } from '../../../../constants/url'
import { useFetchProjects } from '../../../../hooks/useFetchProjects'
import {
  getOptionWarning,
  getTruncatedOptionsWarning,
  sortByWarnings,
} from '../../../../utils/warnings'
import { CustomTargetLanguagesView } from '../../../KeysForLanguage/components/CustomTargetLanguagesView/CustomTargetLanguagesView'
import {
  KeysForLanguageBorderWrapper,
  KeysForLanguageContainer,
  KeysForLanguageContents,
  KeysForLanguageHeader,
  KeysForLanguagesProjectDetails,
  KeysForLanguagesSourceArrow,
  KeysForLanguagesSourceCard,
  KeysForLanguagesTargetLanguagesContainer,
} from '../../../KeysForLanguage/KeysForLanguage'
import { Select } from '../../../Select/Select'
import {
  StepContainer,
  StepContents,
  StepRSP,
  StepSubtitle,
  StepTitle,
  StepTitleContainer,
} from '../../../Step/Step'
import { StyledLink } from '../../../StyledLink/StyledLink'
import styles from './ActivationStep.module.css'

type ActivationStepProps = {
  profileName: string
  integration: Integrations
  onActivate: () => void
  sourceLang: AiConfigLanguage | undefined
  targetLangs: AiConfigLanguage[]
  projects: AiConfigProject[]
  onProjectsChange: (projects: AiConfigProject[]) => void
  onClose: () => void
  isLoading: boolean
  isEdit: boolean
}

export const ActivationStep = ({
  profileName,
  integration,
  projects,
  sourceLang,
  targetLangs,
  isLoading,
  isEdit,
  onProjectsChange,
  onActivate,
  onClose,
}: ActivationStepProps) => {
  const { data: projectOptions, isPending: isLoadingProjects } = useFetchProjects()

  const selectedProjectIds = projects.map(({ id }) => id)
  const selectedOptionsWithBaseLangId =
    projectOptions?.filter((option) => selectedProjectIds.includes(option.id)) ?? []

  const selectedOptionsWithWarningsFirst = sourceLang
    ? sortByWarnings(sourceLang, targetLangs, selectedOptionsWithBaseLangId)
    : []

  return (
    <StepContainer>
      <StepContents>
        <StepTitleContainer>
          <StepTitle>{isEdit ? 'Edit projects' : 'Activate AI profile'}</StepTitle>
          <StepSubtitle>
            Once activated, all translations in the selected projects will be managed by the AI
            profile for your chosen languages.{' '}
            <StyledLink href={AI_PROFILES_DOCUMENTATION_URL} rel="noreferrer" target="_blank">
              Docs.
            </StyledLink>
          </StepSubtitle>
        </StepTitleContainer>
        <Select
          title="Choose projects to activate the AI profile"
          name="projects"
          placeholder="Select..."
          options={projectOptions ?? []}
          selectedOptions={selectedOptionsWithWarningsFirst ?? []}
          onSelectionChange={(projects) => onProjectsChange([...projects])}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          getOptionWarning={(option) =>
            getOptionWarning(
              `${option.baseLangId}`,
              option.targetLanguages,
              sourceLang,
              targetLangs,
            )
          }
          isLoading={isLoadingProjects}
          maxVisibleOptions={5}
          getTruncatedOptionsWarning={(options) =>
            getTruncatedOptionsWarning(sourceLang, options, targetLangs)
          }
        />
        <div className={clsx([styles.footer, 'flex gap-3 items-end justify-end'])}>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onActivate} loading={isLoading}>
            {isEdit ? 'Update' : 'Activate'}
          </Button>
        </div>
      </StepContents>
      <StepRSP>
        <KeysForLanguageContainer>
          <KeysForLanguageBorderWrapper>
            <KeysForLanguageHeader name={profileName} integration={integration} />
            <KeysForLanguageContents>
              <KeysForLanguagesSourceCard sourceLanguage={sourceLang} />

              <KeysForLanguagesSourceArrow />

              <KeysForLanguagesTargetLanguagesContainer>
                <CustomTargetLanguagesView overview targetLangs={targetLangs} />
              </KeysForLanguagesTargetLanguagesContainer>

              <KeysForLanguagesProjectDetails
                highlighted
                projects={selectedOptionsWithBaseLangId}
                hasProjectWarning={(project) =>
                  !!getOptionWarning(
                    `${project.baseLangId}`,
                    project.targetLanguages,
                    sourceLang,
                    targetLangs,
                  )
                }
              />
            </KeysForLanguageContents>
          </KeysForLanguageBorderWrapper>
        </KeysForLanguageContainer>
      </StepRSP>
    </StepContainer>
  )
}
